import { CopyGlyph, ToneIcon } from '../primitives'
import { formatMinor } from '../format'
import type { Cell, ColumnKind, TableBlock, Tone } from '../spec/types'

/**
 * The workhorse — 113 of the corpus's 202 blocks are tables.
 *
 * Verbatim from `docs/design/reference/blocks/table.html`. Structure: a facet
 * bar, a sticky 28px header on a 2px rule, rows at `--k-row` (the density
 * control), and a footer that states the row count and the keyboard hint.
 *
 * Two things here are load-bearing rather than decorative:
 *
 *   - Column widths are the design's own CSS grid tracks, carried in the spec.
 *     A ledger read across screens should land in the same place every time;
 *     auto-sizing columns per screen is how an operator starts mis-reading
 *     rows under time pressure.
 *   - The cell renderer is driven by the COLUMN kind, not by sniffing the
 *     value. An amount renders as an amount because its column says so, which
 *     is why no cell can accidentally lose its currency (invariant 4).
 */

interface Props {
  readonly block: TableBlock
  /** Free-text row filter — shared with the palette, as in the prototype. */
  readonly query: string
  readonly onQuery: (value: string) => void
  /** Index of the row under the keyboard cursor, or -1. */
  readonly cursor: number
  readonly onRowClick: (index: number) => void
  readonly facetsOn: ReadonlySet<string>
  readonly onToggleFacet: (key: string) => void
}

export function Table({
  block,
  query,
  onQuery,
  cursor,
  onRowClick,
  facetsOn,
  onToggleFacet,
}: Props) {
  const gtc = block.cols.map((c) => c[2]).join(' ')
  const needle = query.trim().toLowerCase()
  const rows =
    needle === ''
      ? block.rows
      : block.rows.filter((r) => JSON.stringify(r).toLowerCase().includes(needle))

  const showFacets = block.facets !== false
  const facets = block.facets === false ? [] : (block.facets ?? [])

  return (
    <div style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)' }}>
      {showFacets && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '6px',
            padding: '7px 8px',
            borderBottom: '1px solid var(--k-line)',
            background: 'var(--k-bg)',
          }}
        >
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="filter rows…"
            aria-label={`Filter ${block.title ?? 'rows'}`}
            style={{
              height: '24px',
              width: '150px',
              border: '1px solid var(--k-line)',
              background: 'var(--k-panel)',
              color: 'var(--k-ink)',
              font: 'inherit',
              fontSize: '11.5px',
              padding: '0 7px',
              borderRadius: 0,
            }}
          />
          {facets.map(([k, v], i) => {
            const key = `${block.title ?? ''}${i}`
            return (
              <button
                key={key}
                type="button"
                className="k-hov"
                onClick={() => onToggleFacet(key)}
                data-cur={facetsOn.has(key) ? '1' : ''}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: '24px',
                  padding: '0 8px',
                  border: '1px solid var(--k-line)',
                  background: 'var(--k-panel)',
                  color: 'var(--k-ink)',
                  font: 'inherit',
                  fontSize: '11.5px',
                  cursor: 'pointer',
                }}
              >
                <span style={{ color: 'var(--k-mut2)' }}>{k}</span>
                <span style={{ fontWeight: 600 }}>{v}</span>
              </button>
            )
          })}
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '11px',
              color: 'var(--k-mut2)',
              fontFamily: 'var(--k-mono)',
            }}
          >
            <span>{`${rows.length} of ${block.rows.length} rows`}</span>
            <button type="button" className="k-hov" style={CHIP_BUTTON}>
              Saved view: {block.savedView ?? 'default'}
            </button>
            <button type="button" className="k-hov" style={CHIP_BUTTON}>
              Export
            </button>
          </div>
        </div>
      )}

      {/* The guide builds the table out of divs on a CSS grid, which is what
          gives every screen the same column geometry. Keeping that but adding
          grid roles costs nothing visually and is what makes the rows
          announceable and reachable — an operator clearing a review queue does
          it with j/k and ↵, not by aiming at 23px rows. */}
      <div style={{ overflowX: 'auto' }} role="grid" aria-label={block.title ?? 'Rows'}>
        <div
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: gtc,
            height: '28px',
            alignItems: 'center',
            borderBottom: '2px solid var(--k-rule)',
            background: 'var(--k-panel)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            minWidth: 'min-content',
          }}
        >
          {block.cols.map((c) => (
            <div
              key={c[0]}
              role="columnheader"
              data-a={alignFor(c[1])}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0 9px',
                fontSize: '9.5px',
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                color: 'var(--k-mut)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c[0]}</span>
            </div>
          ))}
        </div>

        {rows.map((row, ri) => (
          <div
            // Row identity is positional in the design corpus; there is no id
            // column to key on and the fixture rows are stable.
            // eslint-disable-next-line react/no-array-index-key
            key={ri}
            role="row"
            tabIndex={0}
            onClick={() => onRowClick(ri)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onRowClick(ri)
              }
            }}
            data-cur={cursor === ri ? '1' : ''}
            className="k-hov"
            style={{
              display: 'grid',
              gridTemplateColumns: gtc,
              alignItems: 'center',
              minHeight: 'var(--k-row, 34px)',
              borderBottom: '1px solid var(--k-line)',
              cursor: 'pointer',
              minWidth: 'min-content',
            }}
          >
            {row.map((cell, ci) => {
              const kind = block.cols[ci]?.[1] ?? 'x'
              return (
                <div
                  // Cells are positional against their column, same reasoning.
                  // eslint-disable-next-line react/no-array-index-key
                  key={ci}
                  role="gridcell"
                  data-a={alignFor(kind)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 9px',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <CellView kind={kind} value={cell} />
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '7px 9px',
          borderTop: '1px solid var(--k-line)',
          background: 'var(--k-bg)',
          fontSize: '11px',
          color: 'var(--k-mut)',
        }}
      >
        <span style={{ fontFamily: 'var(--k-mono)' }}>
          {block.footer ?? `${rows.length} rows · every filter state is in the URL`}
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {block.foot2 ?? 'j / k to move · ↵ to open in inspector'}
        </span>
      </div>
    </div>
  )
}

const CHIP_BUTTON = {
  height: '24px',
  padding: '0 8px',
  border: '1px solid var(--k-line)',
  background: 'var(--k-panel)',
  color: 'var(--k-ink)',
  font: 'inherit',
  fontSize: '11px',
  cursor: 'pointer',
} as const

/** Numbers and amounts right-align. Everything else runs left. */
function alignFor(kind: ColumnKind): string {
  return kind === 'a' || kind === 'n' ? 'r' : ''
}

/**
 * One cell, read according to its column's kind. Transcribed from the
 * prototype's `mkCell` plus the matching branches of the row template.
 */
function CellView({ kind, value }: { kind: ColumnKind; value: Cell }) {
  const arr = Array.isArray(value) ? value : [value]

  if (kind === 'a') {
    const [minor, currency, variant] = arr as [number, string?, string?]
    // An amount column may hold a placeholder — '—' for "not applicable".
    // It renders as text rather than as a zero, because a zero is a claim.
    if (currency === undefined) return <Text value={String(minor)} weight={500} />
    return (
      <>
        <span
          data-amt={variant ?? ''}
          style={{ fontWeight: 600, fontSize: '12.5px', whiteSpace: 'nowrap' }}
        >
          {formatMinor(minor, currency)}
        </span>
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            fontSize: '9.5px',
            color: 'var(--k-mut)',
            letterSpacing: '.03em',
          }}
        >
          {currency}
        </span>
      </>
    )
  }

  if (kind === 'c') {
    const [label, tone] = arr as [string, Tone?]
    return (
      <span
        data-tone={tone ?? 'n'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '1px 5px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '.03em',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Only critical and attention carry a glyph — a chip that always had
            one would stop drawing the eye to the ones that matter. */}
        {(tone === 'r' || tone === 'a') && <ToneIcon tone={tone} size={10} width={2.6} />}
        <span>{label}</span>
      </span>
    )
  }

  if (kind === 'm') {
    return (
      <>
        <span
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontFamily: 'var(--k-mono)',
            fontSize: '11px',
            color: 'var(--k-mut)',
          }}
        >
          {String(arr[0])}
        </span>
        <CopyGlyph />
      </>
    )
  }

  if (kind === 'b') {
    const [label, pct, tone] = arr as [string, number, Tone?]
    return (
      <>
        <span
          style={{
            flex: 1,
            height: '6px',
            background: 'var(--k-panel3)',
            minWidth: '40px',
          }}
        >
          <span
            data-bar={tone ?? 'n'}
            style={{ display: 'block', height: '6px', width: `${pct}%` }}
          />
        </span>
        <span
          style={{
            fontFamily: 'var(--k-mono)',
            fontSize: '10.5px',
            color: 'var(--k-mut)',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </>
    )
  }

  if (kind === 's') {
    const [text, sub] = arr as [string, string?]
    return (
      <>
        <Text value={text} weight={600} />
        {sub !== undefined && sub !== '' && (
          <span
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontSize: '11px',
              color: 'var(--k-mut)',
            }}
          >
            {sub}
          </span>
        )}
      </>
    )
  }

  return <Text value={String(arr[0])} weight={kind === 'x2' ? 600 : 500} />
}

function Text({ value, weight }: { value: string; weight: number }) {
  return (
    <span
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '12px',
        fontWeight: weight,
      }}
    >
      {value}
    </span>
  )
}
